import type * as Core from "@bilda/core";
import type { ExternalError } from "../../app/externalError.js";
import type {
    AuthorEntry,
    ChatEntry,
    DraftingPhase,
    LiveActivity,
    SituationProfileChipView,
} from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { SendTurnEffect } from "./effect.js";

// The composer's message-history cursor, per conversation (A-11). Arrow-Up/Down
// walk the author's previously-sent messages (shell/readline history). A sum,
// never a boolean+index pair, so an index is unrepresentable while at rest:
//   - `at_draft` — resting on the live draft; no recall in progress.
//   - `at_history` — mid-recall at `index` (0 = newest sent message), holding
//     the `savedDraft` captured once at the `at_draft → at_history` transition
//     so it is restored intact on returning past the newest entry.
export type ComposerHistoryNav =
    | { readonly type: "at_draft" }
    | {
          readonly type: "at_history";
          readonly index: number;
          readonly savedDraft: string;
      };

// What the composer's Retry re-sends after a failed turn (F113). A sum so a
// failed FORM RESUME retries the resume (re-marked, no author bubble) instead
// of re-sending the answers as a plain message. Arrow-Up recall never reads
// this — it walks the transcript's author rows only.
type NothingSent = { readonly type: "nothing_sent" };
type LastSentText = {
    readonly type: "last_sent_text";
    readonly text: string;
};
type LastSentFormResume = {
    readonly type: "last_sent_form_resume";
    readonly requestId: string;
    readonly text: string;
};
type LastSent = NothingSent | LastSentText | LastSentFormResume;

// Whether the conversation exists server-side yet. A NASCENT conversation (a
// client-minted id, nothing durable) is started by its first send; the root
// resolves this at intent time from the conversation list (plan §1.3).
export type ConversationStanding = "nascent" | "durable";

// A turn whose port send waits. The bubble shows at once; the send goes out
// when what it waits for has settled:
//  - `awaiting_uploads`: an attached file is still uploading, so the turn
//    carries the landed source as a real mention (a durable chip, and the
//    agent sees the source in this very turn). `standing` says whether the
//    conversation still has to be started once they land.
//  - `awaiting_start`: the conversation's durable start (and the flush of its
//    pre-send grounding) is under way.
type HeldSend =
    | { readonly type: "no_held_send" }
    | {
          readonly type: "awaiting_uploads";
          // The author bubble whose chips settle as the uploads land.
          readonly entryId: string;
          readonly send: SendTurnEffect;
          readonly standing: ConversationStanding;
      }
    | { readonly type: "awaiting_start"; readonly send: SendTurnEffect };

// Whether this client asked for a conversation's durable transcript + profile.
// `requested` covers in flight and loaded alike (a visible conversation loads
// once per session); a failed transcript load returns to `unrequested` so the
// next visibility report (or world re-read) retries it.
type TranscriptLoad = "unrequested" | "requested";

// Why a turn failed, as data: the view and the toast localize it (chat holds
// no user-facing strings).
export type TurnFailure =
    | { readonly type: "port_refused"; readonly error: ExternalError }
    | { readonly type: "conversation_not_started" };

// The resume turn that continues a conversation once a form answer persisted
// (F12/F99). `text` is the rendered answers (or the locale-free fallback); a
// resume carries no @-mentions of its own. `target` and `anchor` are resolved
// by the root when the answer is submitted.
export type FormResume = {
    readonly requestId: string;
    readonly text: string;
    readonly target: string | null;
    readonly anchor: Core.EntityRef.Ref | null;
};

// How one attached upload settled.
export type UploadOutcome =
    | { readonly type: "upload_landed"; readonly source: ComposerResourceRef }
    | { readonly type: "upload_failed" };

// Settle one upload chip (by its optimistic id): a landed upload becomes the
// real source chip; a failed one stays, marked `failed`.
export const settleUploadChip = (
    mentions: readonly ComposerResourceRef[],
    chipId: string,
    outcome: UploadOutcome,
): readonly ComposerResourceRef[] =>
    mentions.map((m) =>
        m.id !== chipId
            ? m
            : outcome.type === "upload_landed"
              ? outcome.source
              : { ...m, status: "failed" },
    );

export const isAwaitingUpload = (
    mentions: readonly ComposerResourceRef[],
): boolean => mentions.some((m) => m.status === "uploading");

// What rides the port send: a failed upload attached nothing.
export const sendableMentions = (
    mentions: readonly ComposerResourceRef[],
): readonly ComposerResourceRef[] =>
    mentions.filter((m) => m.status !== "failed");

export type ChatSession = {
    readonly conversation: readonly ChatEntry[];
    readonly draft: string;
    readonly drafting: boolean;
    // D51 — the latest live activity the turn stream reported (transient,
    // never reloaded). Cleared when a turn begins; read only while drafting.
    readonly liveActivity: LiveActivity | null;
    readonly situationProfile: SituationProfileChipView;
    readonly turnError: TurnFailure | null;
    readonly lastSent: LastSent;
    readonly historyNav: ComposerHistoryNav;
    // The local-entry id counter (S4). Per conversation, so independent
    // conversations' turns commute; ids carry the conversation id, so they
    // stay unique across the whole client too.
    readonly nextLocalId: number;
    readonly heldSend: HeldSend;
    readonly transcriptLoad: TranscriptLoad;
};

// The transcript's trailing status, projected from the session (D51): while a
// turn is in flight it carries the latest live activity, so the row says what
// the agent is doing; at rest there is no row. Before the stream's first
// activity the agent is planning: the turn has begun and the model works
// before its first output (and a round's first `thinking` can be published
// before the stream connects, so waiting for it would leave a generic label).
export const draftingPhaseOf = (
    session: ChatSession,
): DraftingPhase | undefined =>
    session.drafting
        ? {
              type: "drafting",
              activity: session.liveActivity ?? { type: "thinking" },
          }
        : undefined;

export const emptyChatSession = (): ChatSession => ({
    conversation: [],
    draft: "",
    drafting: false,
    liveActivity: null,
    situationProfile: { type: "no_situation_profile" },
    turnError: null,
    lastSent: { type: "nothing_sent" },
    historyNav: { type: "at_draft" },
    nextLocalId: 0,
    heldSend: { type: "no_held_send" },
    transcriptLoad: "unrequested",
});

// Local ids are a pure function of the session's counter (S4): decide reads
// them, evolve advances the counter past them on the event that consumed them.
// No clock, no randomness. A submitted turn needs two (author bubble +
// status/tool trail); a form resume one (trail only; it renders no bubble).
export type TurnLocalIds = {
    readonly entryId: string;
    readonly activityId: string;
};
export const turnLocalIds = (
    conversationId: string,
    session: ChatSession,
): TurnLocalIds => ({
    entryId: `local-author-${conversationId}-${session.nextLocalId}`,
    activityId: `local-activity-${conversationId}-${session.nextLocalId + 1}`,
});
export const afterTurnLocalIds = (session: ChatSession): number =>
    session.nextLocalId + 2;
export const resumeActivityId = (
    conversationId: string,
    session: ChatSession,
): string => `local-activity-${conversationId}-${session.nextLocalId}`;
export const afterResumeActivityId = (session: ChatSession): number =>
    session.nextLocalId + 1;

// The activity id of the session's most recent turn. Both `turn_started` and
// `turn_resumed` leave the counter exactly one past the activity id they
// consumed, so it is `nextLocalId - 1` (none before the first turn). A Stop
// fences it so the stopped turn's late progress/reply/failure is dropped even
// when nothing streamed yet. Fencing an already-settled turn's id is harmless.
export const latestTurnActivityIds = (
    conversationId: string,
    session: ChatSession,
): readonly string[] =>
    session.nextLocalId === 0
        ? []
        : [`local-activity-${conversationId}-${session.nextLocalId - 1}`];

// The author's own previously-sent messages, newest first (index 0 = the most
// recently sent). Derived purely from the transcript's `author` rows — no new
// store; index 0 is the newest typed message. SHORTCUT (MVP): never pruned or
// capped — grows with the conversation (same shortcut as `cancelledMessageIds`); worth a cap (~200) if
// conversations ever get materially longer.
export const authorMessageHistory = (
    conversation: readonly ChatEntry[],
): readonly string[] =>
    conversation
        .filter((e): e is AuthorEntry => e.type === "author")
        .map((e) => e.text)
        .reverse();

export const chatSessionFor = (
    sessions: ReadonlyMap<string, ChatSession>,
    conversationId: string | null,
): ChatSession =>
    conversationId === null
        ? emptyChatSession()
        : (sessions.get(conversationId) ?? emptyChatSession());

export const updateChatSession = (
    sessions: ReadonlyMap<string, ChatSession>,
    conversationId: string,
    update: (session: ChatSession) => ChatSession,
): ReadonlyMap<string, ChatSession> => {
    const next = new Map(sessions);
    next.set(
        conversationId,
        update(sessions.get(conversationId) ?? emptyChatSession()),
    );
    return next;
};

export const visibleConversationIds = (
    leaves: readonly {
        readonly active: { readonly type: string; readonly id?: string };
    }[],
    loadable: (id: string) => string | null,
): readonly string[] => [
    ...new Set(
        leaves.flatMap((leaf) => {
            if (
                leaf.active.type !== "conversation" ||
                leaf.active.id === undefined
            )
                return [];
            const id = loadable(leaf.active.id);
            return id === null ? [] : [id];
        }),
    ),
];
